import { fromJS } from 'immutable';
import { getDefault } from '@pearsontechnology/aero-plugin-api';
import PluginService from 'services/PluginService';
import * as manifestKeyPaths from '../manifest/keyPaths';
import { loadPluginView, loadErrorView } from './loadPluginView';
import Multimap from '../helpers/Multimap';
import { generateNonce } from './idProvider';
import invokePlugin from './invokePlugin';
import { LEARNER } from '../constants/context';

const { ApiDriver } = getDefault();

// coursewareId -> info
export const activePlugins = new Map();

// coursewareId -> [Array of ancestry Id's]
export const coursewareAncestry = new Map();

// parentId -> childId
export const parentToChild = new Multimap();

const replaceSpaceWithHyphen = (val) => val ? val.replace(/\s/g, '-').toLowerCase() : '';

// Shared code of activity, interactive, component
export async function startPluginShared(instanceId, manifest, element, context, pluginId, coursewareType, pluginTitle, pluginName) {
  const entryPoint = await extractEntryPoint(manifest, pluginId, context);

  return loadPluginView(entryPoint.toJS(), element)
    .then((managedIframe) => {
      if (managedIframe) { // if text/html was loaded
        const driver = ApiDriver.listenForConnectionsInWorkspace(
          instanceId,
          managedIframe.getIframe(),
          context,
          coursewareType,
        );

        if (pluginTitle) {
          managedIframe.addTitle(pluginTitle);
        }
        managedIframe.addClass(`plugin-${replaceSpaceWithHyphen(pluginName)}`);
        managedIframe.load();
        return driver;
      }
      return null;
    }).catch(() => {
      loadErrorView(entryPoint.toJS(), element);
    });
}

export async function startPluginInitChild(manifest, pluginId, instanceId, coursewareType, respondToInitChildCallback, pluginTitle, pluginName) {
  const activePlugin = activePlugins.get(instanceId);
  const { context } = activePlugin;

  const entryPoint = await extractEntryPoint(manifest, pluginId, context);
  const nonce = generateNonce();
  const driver = ApiDriver.listenForNonceInWorkspace(
    instanceId,
    nonce,
    context,
    coursewareType);
  activePlugin.driver = driver;

  respondToInitChildCallback({
    nonce,
    entryPoint: entryPoint.toJS(),
    title: pluginTitle,
    name: pluginName,
  });
}

async function extractEntryPoint(manifest, pluginId, context) {
  let entryPoints = manifest.getIn(manifestKeyPaths.entryPoints);

  if (!entryPoints) {
    const plugin = await PluginService.getPlugin(
      manifest.getIn(['id']),
      manifest.getIn(['version'])
      /* , context */
    );
    entryPoints = fromJS(plugin.entryPoints);
  }

  const entryPoint = entryPoints.get(context) || entryPoints.get(LEARNER) || entryPoints.first();
  return entryPoint;
}

// CREATE CHILDREN

export function initChildWithCallback(id, childId, childType, manifest, context, respondToInitChildCallback) {
  parentToChild.add(id, childId);
  invokePlugin(
    childId,
    childType,
    manifest,
    null,
    context,
    respondToInitChildCallback
  );
  const parentAncestry = coursewareAncestry.get(id) || [];
  coursewareAncestry.set(childId, [...parentAncestry, id]);
}

// REMOVE PLUGIN / CHILDREN

function _getChildIds(instanceId) {
  let children;
  if (parentToChild.has(instanceId)) {
    children = parentToChild.get(instanceId);
  } else {
    children = [];
  }
  return children;
}

function _removePlugin(instanceId) {
  if (!activePlugins.has(instanceId)) {
    return;
  }

  const entry = activePlugins.get(instanceId);
  const { driver } = entry;
  if (driver) {
    driver.cancelListenersAndSubscriptions();
  }

  // remove from register
  activePlugins.delete(instanceId);
}

function removeDescendants(id) {
  const childIds = [..._getChildIds(id)];
  childIds.forEach((childId) => removeChild(id, childId));
}

export function removePluginInstance(instanceId) {
  removeDescendants(instanceId);
  _removePlugin(instanceId);
  coursewareAncestry.delete(instanceId);
}

export async function removeChild(instanceId, childId) {
  const children = _getChildIds(instanceId);

  if (children && children.indexOf(childId) !== -1) {
    removeDescendants(childId);
    _removePlugin(childId);
    coursewareAncestry.delete(childId);
    children.splice(children.indexOf(childId), 1);
    if (children.length === 0) {
      parentToChild.delete(instanceId);
    }
  }
}